import { useMemo, useState, type ReactNode } from 'react';

import { GEN_KIND_LABEL, generatorById, type GenRequest } from '@ai-studio/shared';

import {
  DECIDER_NOTE,
  ageLabel,
  defaultRefineStages,
  generatorsOf,
  groupRequests,
  logTail,
  pipelineOf,
  progressFraction,
  sameStages,
  STATUS_LABEL,
  videoRefineOptions,
  type Pipeline,
} from '../../lib/generation.js';
import { currentDecider, useGeneration, type DecisionInput } from '../../state/generation.js';
import { useWorkspace } from '../../state/workspace.js';
import { NumberField, SelectField } from '../ui/controls.js';
import { StageTag } from '../ui/GeneratorTag.js';
import { Icon } from '../ui/icons.js';
import { useGenerationFeed } from './hooks.js';
import { PipelineEditor } from './PipelineEditor.js';
import { GenerationPreview } from './previews/index.js';
import './generation.css';

function RequestHead({ request }: { request: GenRequest }) {
  return (
    <>
      <div className="gen-card-head">
        <span className={`gen-kind gen-kind-${request.kind}`}>{GEN_KIND_LABEL[request.kind]}</span>
        <strong className="gen-card-title" title={request.prompt}>
          {request.title}
        </strong>
        <span className={`gen-status gen-status-${request.status}`}>{STATUS_LABEL[request.status]}</span>
      </div>
      <div className="gen-card-meta">
        <span className="gen-stages-line" title={request.stages.join(' -> ')}>
          {request.stages.map((id, i) => (
            <span key={`${id}-${i}`} className="gen-stage-chip">
              {generatorById(id)?.label ?? id}
              <StageTag id={id} />
            </span>
          ))}
        </span>
        <span className="muted">
          by {request.requestedBy} · {ageLabel(request.updatedAt)}
          {request.refineOf ? ' · refines an earlier result' : ''}
          {request.attempt && request.attempt > 1 ? ` · attempt ${request.attempt}` : ''}
        </span>
        <code className="gen-out" title={request.out}>
          {request.out}
        </code>
      </div>
    </>
  );
}

/** The redo form: change the generator / chain, prompt or seed, then run again. */
function RedoForm({ request, onSubmit, onCancel, busy }: { request: GenRequest; onSubmit: (input: DecisionInput) => void; onCancel: () => void; busy: boolean }) {
  const [prompt, setPrompt] = useState(request.prompt);
  const [seed, setSeed] = useState<number | undefined>(typeof request.args?.seed === 'number' ? (request.args.seed as number) : undefined);
  const [pipeline, setPipeline] = useState<Pipeline>(() => pipelineOf(request.stages, request.stageParams));
  const editable = request.kind === 'model' || request.kind === 'pixel';
  const audioChoices = generatorsOf(request.kind);

  function submit() {
    const input: DecisionInput = {};
    if (prompt.trim() && prompt !== request.prompt) input.prompt = prompt.trim();
    if (seed !== undefined) input.seed = seed;
    const changed = !sameStages(pipeline.stages, request.stages);
    if (changed) input.stages = [...pipeline.stages];
    if (request.kind === 'pixel') input.stageParams = pipeline.params.map((p) => ({ ...p }));
    onSubmit(input);
  }

  return (
    <div className="gen-redo">
      <label className="gen-field">
        <span className="muted">Prompt</span>
        <textarea rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
      </label>
      {editable ? (
        <PipelineEditor mode={request.kind as 'model' | 'pixel'} pipeline={pipeline} onChange={setPipeline} showParams={request.kind === 'pixel'} />
      ) : (
        audioChoices.length > 1 && (
          <label className="gen-field">
            <span className="muted">Engine</span>
            <SelectField
              value={pipeline.stages[0] ?? audioChoices[0]!.id}
              options={audioChoices.map((g) => ({ value: g.id, label: g.label }))}
              onChange={(id) => setPipeline(pipelineOf([id]))}
            />
          </label>
        )
      )}
      <div className="gen-field gen-seed">
        <span className="muted">Seed</span>
        <NumberField value={seed} onChange={setSeed} min={0} max={4294967295} step={1} integer title="Leave empty for a new random seed" />
        <button type="button" className="link-btn" onClick={() => setSeed(undefined)} disabled={seed === undefined}>
          New random
        </button>
      </div>
      <div className="row-actions">
        <button type="button" className="ghost small" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="primary small" disabled={busy || (editable && pipeline.stages.length === 0)} onClick={submit}>
          Redo
        </button>
      </div>
    </div>
  );
}

/** The refine form: keep the result and build on it (more stages, or a changed prompt / seed). */
function RefineForm({ request, onSubmit, onCancel, busy }: { request: GenRequest; onSubmit: (input: DecisionInput) => void; onCancel: () => void; busy: boolean }) {
  const stagesDefault = defaultRefineStages(request.kind);
  const staged = stagesDefault !== null;
  // Audio: a variant with a tweaked prompt, so the field starts with the original one.
  const [instructions, setInstructions] = useState(staged ? '' : request.prompt);
  const [seed, setSeed] = useState<number | undefined>(undefined);
  const [pipeline, setPipeline] = useState<Pipeline>(() => pipelineOf(stagesDefault ?? []));
  const clipOptions = videoRefineOptions(request);
  const [build, setBuild] = useState('');

  function submit() {
    const input: DecisionInput = {};
    const text = instructions.trim();
    if (text) {
      input.prompt = text;
      if (staged) input.note = text;
    }
    if (seed !== undefined) input.seed = seed;
    if (staged) {
      input.stages = [...pipeline.stages];
      if (request.kind === 'pixel') input.stageParams = pipeline.params.map((p) => ({ ...p }));
    }
    if (clipOptions.length && build) {
      input.stages = [build];
      input.note = text || `Builds on the clip: ${build}.`;
    }
    onSubmit(input);
  }

  return (
    <div className="gen-redo">
      <p className="gen-note">
        {request.kind === 'model'
          ? "Keeps this model; its render is fed into the stages below."
          : request.kind === 'pixel'
            ? 'Keeps this image; it goes through the stages below.'
            : request.kind === 'music'
              ? 'Keeps this track as the reference for a variation.'
              : request.kind === 'video'
                ? 'Keeps this clip: extend it, interpolate or upscale it, or make a new take with your feedback.'
              : 'Keeps this result and makes a variant.'}
      </p>
      {staged && <PipelineEditor mode={request.kind as 'model' | 'pixel'} pipeline={pipeline} onChange={setPipeline} showParams={request.kind === 'pixel'} refine />}
      {clipOptions.length > 0 && (
        <label className="gen-field">
          <span className="muted">Build on the clip</span>
          <SelectField value={build} onChange={setBuild} options={[{ value: '', label: 'No: a new take with the feedback below' }, ...clipOptions]} />
        </label>
      )}
      <label className="gen-field">
        <span className="muted">{staged ? 'What to change or improve' : 'Prompt'}</span>
        <textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
      </label>
      <div className="gen-field gen-seed">
        <span className="muted">Seed</span>
        <NumberField value={seed} onChange={setSeed} min={0} max={4294967295} step={1} integer title="Leave empty for a new random seed" />
        <button type="button" className="link-btn" onClick={() => setSeed(undefined)} disabled={seed === undefined}>
          New random
        </button>
      </div>
      <div className="row-actions">
        <button type="button" className="ghost small" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="primary small" disabled={busy || (staged && pipeline.stages.length === 0)} onClick={submit}>
          Refine
        </button>
      </div>
    </div>
  );
}

/** One request with the actions the user may take on it. */
export function RequestCard({ request, projectId, readOnly }: { request: GenRequest; projectId: number; readOnly: boolean }) {
  const decide = useGeneration((s) => s.decide);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<'redo' | 'refine' | null>(null);

  async function act(action: Parameters<typeof decide>[1], input?: DecisionInput) {
    setBusy(true);
    const ok = await decide(request.id, action, input);
    setBusy(false);
    if (ok) setForm(null);
  }

  const fraction = request.status === 'running' ? progressFraction(request.progress) : null;
  const tail = request.status === 'running' ? logTail(request) : [];

  return (
    <article className={`gen-card gen-card-${request.status}`}>
      <RequestHead request={request} />
      {request.status === 'queued' && (
        <p className="muted gen-line">
          Queued{typeof request.ahead === 'number' ? `: ${request.ahead} ahead of it` : ''}. One local generation runs at a time across all projects.
        </p>
      )}
      {request.status === 'running' && (
        <div className="gen-progress-wrap">
          <div className="gen-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={fraction === null ? undefined : Math.round(fraction * 100)}>
            <span className={fraction === null ? 'is-indeterminate' : ''} style={fraction === null ? undefined : { width: `${fraction * 100}%` }} />
          </div>
          {request.progress && <span className="muted gen-line">{request.progress}</span>}
          {tail.length > 0 && <pre className="gen-log">{tail.join('\n')}</pre>}
        </div>
      )}
      {request.status === 'failed' && request.error && <p className="gen-error">{request.error}</p>}
      {request.note && <p className="muted gen-line">{request.note}</p>}
      {request.status === 'review' && <GenerationPreview request={request} projectId={projectId} />}

      {request.status === 'awaiting-approval' && !readOnly && (
        <div className="row-actions">
          <button type="button" className="ghost small" disabled={busy} onClick={() => void act('reject')}>
            Reject
          </button>
          <button type="button" className="primary small" disabled={busy} onClick={() => void act('approve')}>
            Approve
          </button>
        </div>
      )}
      {request.status === 'review' && !readOnly && form === null && (
        <div className="row-actions">
          <button type="button" className="ghost small" disabled={busy} onClick={() => void act('deny')} title="Throw the result away (the previous file is restored)">
            Deny
          </button>
          <button type="button" className="ghost small" disabled={busy} onClick={() => setForm('redo')} title="Start over from the original request, optionally with another generator, chain, prompt or seed (replaces the result)">
            Redo...
          </button>
          <button type="button" className="ghost small" disabled={busy} onClick={() => setForm('refine')} title="Keep this result and build on it: it becomes the input of the next stages">
            Refine...
          </button>
          <button type="button" className="primary small" disabled={busy} onClick={() => void act('accept')}>
            Accept
          </button>
        </div>
      )}
      {request.status === 'review' && !readOnly && form === 'redo' && <RedoForm request={request} busy={busy} onCancel={() => setForm(null)} onSubmit={(input) => void act('redo', input)} />}
      {request.status === 'review' && !readOnly && form === 'refine' && <RefineForm request={request} busy={busy} onCancel={() => setForm(null)} onSubmit={(input) => void act('refine', input)} />}
    </article>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  if (count === 0) return null;
  return (
    <section className="gen-section">
      <h4>
        {title} <span className="badge gen-count">{count}</span>
      </h4>
      {children}
    </section>
  );
}

/** "Generations": what needs the user first, then what is running, queued and done. */
export function GenerationInbox({ active = true }: { active?: boolean }) {
  useGenerationFeed(active);
  const projectId = useWorkspace((s) => s.projectId);
  const requests = useGeneration((s) => s.requests);
  const queue = useGeneration((s) => s.queue);
  const serverDecider = useGeneration((s) => s.decider);
  const error = useGeneration((s) => s.error);
  const groups = useMemo(() => groupRequests(requests), [requests]);
  if (projectId === null) return <p className="muted explorer-hint">No project open.</p>;
  const who = serverDecider ?? currentDecider();
  const readOnly = who !== 'human';
  const empty = requests.length === 0 && queue.length === 0;

  return (
    <div className="gen-inbox">
      <p className={`gen-decider gen-decider-${who}`}>
        <Icon name={who === 'human' ? 'cursor' : who === 'assistant' ? 'sparkles' : 'check'} size={12} /> {DECIDER_NOTE[who]}
      </p>
      {error && (
        <p className="gen-error" role="alert">
          {error}
        </p>
      )}
      {empty && <p className="muted gen-empty">No local generations yet. They appear here when you or the assistant start one (3D models, pixel art, speech, music, sound effects).</p>}

      <Section title={readOnly ? 'Awaiting approval' : 'Needs your approval'} count={groups.approval.length}>
        {groups.approval.map((r) => (
          <RequestCard key={r.id} request={r} projectId={projectId} readOnly={readOnly} />
        ))}
      </Section>
      <Section title={readOnly ? 'In review' : 'Review the result'} count={groups.review.length}>
        {groups.review.map((r) => (
          <RequestCard key={r.id} request={r} projectId={projectId} readOnly={readOnly} />
        ))}
      </Section>
      <Section title="Running and queued" count={groups.active.length}>
        {groups.active.map((r) => (
          <RequestCard key={r.id} request={r} projectId={projectId} readOnly />
        ))}
      </Section>
      {queue.length > 0 && (
        <section className="gen-section">
          <h4>
            Machine queue <span className="badge gen-count">{queue.length}</span>
          </h4>
          <ul className="gen-queue">
            {queue.map((q) => (
              <li key={q.file}>
                <span className="gen-queue-pos">#{q.position + 1}</span>
                <span className={`gen-kind gen-kind-${q.kind}`}>{GEN_KIND_LABEL[q.kind]}</span>
                <span className="gen-queue-label" title={q.label}>
                  {q.label}
                </span>
              </li>
            ))}
          </ul>
          <p className="muted gen-line">All projects share this queue; jobs run first come, first served.</p>
        </section>
      )}
      {groups.history.length > 0 && (
        <details className="gen-section gen-history">
          <summary>
            History <span className="badge gen-count">{groups.history.length}</span>
          </summary>
          {groups.history.slice(0, 40).map((r) => (
            <RequestCard key={r.id} request={r} projectId={projectId} readOnly />
          ))}
        </details>
      )}
    </div>
  );
}

/** Compact list of requests with an optional "Open" button (used under the pixel and sound generate panels). */
export function RequestList({
  requests,
  projectId,
  onOpen,
  openLabel = 'Open',
}: {
  requests: readonly GenRequest[];
  projectId: number;
  onOpen?: (request: GenRequest) => void;
  openLabel?: string;
}) {
  const serverDecider = useGeneration((s) => s.decider);
  const who = serverDecider ?? currentDecider();
  return (
    <div className="gen-mini-list">
      {requests.map((r) => (
        <div key={r.id} className="gen-mini">
          <RequestCard request={r} projectId={projectId} readOnly={who !== 'human'} />
          {onOpen && (r.status === 'review' || r.status === 'accepted') && (
            <button type="button" className="primary small gen-open" onClick={() => onOpen(r)}>
              {openLabel}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
